import { useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { Elements, ExpressCheckoutElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { PaymentIntentStart } from '../lib/types';
import { money } from '../lib/types';

const stripeCache = new Map<string, Promise<Stripe | null>>();
function stripeFor(publishableKey: string): Promise<Stripe | null> {
  let p = stripeCache.get(publishableKey);
  if (!p) { p = loadStripe(publishableKey); stripeCache.set(publishableKey, p); }
  return p;
}

interface Props {
  intent: PaymentIntentStart;
  returnUrl: string;
  primaryColor: string;
  onPaid: () => Promise<void> | void;
  onCancel: () => Promise<void> | void;
}

/** Stripe Payment Element bound to a PaymentIntent the gateway created. */
export function PaymentSheet({ intent, returnUrl, primaryColor, onPaid, onCancel }: Props) {
  const stripePromise = useMemo(() => stripeFor(intent.publishableKey), [intent.publishableKey]);
  return (
    <div className="sheet-backdrop">
      <div className="sheet" role="dialog" aria-modal="true" aria-label="Pay">
        <div className="sheet-body">
          <h3>Pay {money(intent.amountCents, intent.currency)}</h3>
          {!intent.livemode && <p className="muted small">Test mode — no real charge.</p>}
          <Elements stripe={stripePromise} options={{
            clientSecret: intent.clientSecret,
            appearance: { theme: 'stripe', variables: { colorPrimary: primaryColor } },
          }}>
            <PayForm returnUrl={returnUrl} onPaid={onPaid} onCancel={onCancel} />
          </Elements>
        </div>
      </div>
    </div>
  );
}

function PayForm({ returnUrl, onPaid, onCancel }: Pick<Props, 'returnUrl' | 'onPaid' | 'onCancel'>) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [hasWallet, setHasWallet] = useState(false);

  const confirm = async () => {
    if (!stripe || !elements) return false;
    setBusy(true); setError(null);
    const r = await stripe.confirmPayment({ elements, confirmParams: { return_url: returnUrl }, redirect: 'if_required' });
    if (r.error) { setError(r.error.message || 'Payment failed.'); setBusy(false); return false; }
    try { await onPaid(); } finally { setBusy(false); }
    return true;
  };

  const submit = async (e: React.FormEvent) => { e.preventDefault(); await confirm(); };

  return (
    <form onSubmit={submit} className="payform">
      {/* Apple Pay / Google Pay / Link buttons — Stripe shows whichever the
          browser + the store's account support (Apple Pay needs Safari and the
          domain registered on the store's account, see admin Settings). */}
      <div className={hasWallet ? 'wallets' : 'wallets wallets-empty'}>
        <ExpressCheckoutElement
          options={{ buttonType: { applePay: 'check-out', googlePay: 'checkout' }, buttonHeight: 48, layout: { maxColumns: 2, overflow: 'never' } }}
          onReady={(ev) => setHasWallet(!!ev.availablePaymentMethods)}
          onConfirm={async (ev) => { const ok = await confirm(); if (!ok) ev.paymentFailed({ reason: 'fail' }); }}
        />
        {hasWallet && <div className="wallets-or"><span>or pay with card</span></div>}
      </div>
      <PaymentElement options={{ layout: 'tabs', wallets: { applePay: 'never', googlePay: 'never' } }} />
      {error && <p className="error">{error}</p>}
      <div className="row between" style={{ marginTop: 16 }}>
        <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void onCancel()}>Cancel</button>
        <button type="submit" className="btn" disabled={!stripe || busy}>{busy ? 'Paying…' : 'Pay'}</button>
      </div>
    </form>
  );
}
